import { useEffect, useState } from 'react';

import NotificationApi from 'api/notificationApi';
import IconButton from 'components/atoms/button/IconButton';
import { NotificationData } from 'types/notification';
import './NotificationButton.scss';
import useSubscribeUserSse from '../../../utils/hooks/useSubscribeUserSse';
import clsx from 'clsx';
import { User } from 'firebase/auth';
import useOnclickOutside from 'react-cool-onclickoutside';
import NotificationService from '../../../services/notificationService';
import NotificationCard from './NotificationCard';

interface NotificationButtonProp {
  user: User;
}

function NotificationButton({ user }: NotificationButtonProp) {
  const [notifications, setNotifications] = useState<NotificationData[]>([]);
  const [showNotificationCard, setShowNotificationCard] = useState<boolean>(false);
  const newNotificationId = useSubscribeUserSse<string>('NOTIFICATION', user.uid);
  const unreadNotifications = notifications.filter(notification => !notification.read);

  const notificationCardRef = useOnclickOutside(() => setShowNotificationCard(false), { eventTypes: ['mouseup'] });

  useEffect(() => {
    async function fetchData() {
      const fetchedNotifications = await NotificationApi.getNotificationsForUser(user.uid);
      setNotifications(fetchedNotifications);
    }

    fetchData();
  }, [user]);

  // Add incoming realtime notification
  useEffect(() => {
    async function fetchNewNotification() {
      if (newNotificationId) {
        const newNotification = await NotificationApi.getNotification(newNotificationId);
        setNotifications(prev => [...prev, newNotification]);
      }
    }
    fetchNewNotification();
  }, [newNotificationId]);

  function toggleShowNotificationCard() {
    setShowNotificationCard(showNotificationCard => !showNotificationCard);
  }

  async function handleNotificationRead(notification: NotificationData) {
    const updatedNotifications = await NotificationService.markNotificationAsReadInArray(notification, notifications);
    setNotifications(updatedNotifications);
  }

  async function handleMarkAllAsRead() {
    const updatedNotifications = await NotificationService.markAllNotificationsAsReadInArray(notifications);
    setNotifications(updatedNotifications);
  }

  return (
    <div ref={notificationCardRef} className="notification-container">
      <IconButton
        className={clsx('notification-button', unreadNotifications.length > 0 && 'unread-notifications')}
        onClick={toggleShowNotificationCard}
        icon="mdi:bell-outline"
        visualType="no-bg"
      />
      {showNotificationCard && (
        <NotificationCard
          notifications={notifications}
          onNotificationRead={handleNotificationRead}
          onMarkAllAsRead={handleMarkAllAsRead}
          onClose={() => setShowNotificationCard(false)}
          hidden={!showNotificationCard}
        />
      )}
    </div>
  );
}

export default NotificationButton;
